import React from 'react'
import {
  faCircleCheck,
  faClock,
  faTriangleExclamation,
} from '@fortawesome/pro-regular-svg-icons'
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'

import { Icon, List, Text } from '@/ui'
import { TextColors, TypographyStyles } from '@/styles'

/**
 * A mock of the report's own hero card (`ReportHero` in `ReportView`), with
 * invented numbers. Deliberately a rebuild rather than a screenshot: no hosted
 * asset to produce, nothing to go stale, and it stays legible at any width.
 *
 * The tiles mirror the real hero's accents exactly (`COUNT_TILE_META` there),
 * so nothing here promises a color or a shape the report doesn't deliver.
 * `needs_info` (the blue "tell us more" tile) is omitted because it only
 * renders when that count is non-zero, and a clean sample is the common case.
 */
const TILES: {
  count: number
  label: string
  icon: IconDefinition
  color: string
  bg: string
}[] = [
  {
    count: 3,
    label: 'need a look',
    icon: faTriangleExclamation,
    color: 'text-orange-200',
    bg: 'bg-orange-25',
  },
  {
    count: 9,
    label: 'covered',
    icon: faCircleCheck,
    color: 'text-green-100',
    bg: 'bg-green-25',
  },
  {
    count: 2,
    label: 'worth watching',
    icon: faClock,
    color: 'text-neutral-500',
    // The report's neutral tile uses the Icon atom's `level2` background,
    // which resolves to this. Named directly here because this mock renders
    // the swatch itself rather than going through the atom.
    bg: 'bg-neutral-50',
  },
]

const POINTS: { title: string; body: string }[] = [
  {
    title: 'What we ask',
    body: 'The basics of how your practice is set up and run.',
  },
  {
    title: 'How long',
    body: '5 minutes, and only what applies to you.',
  },
  {
    title: 'What you get',
    body: 'A clear picture of where you stand, and what to focus on first.',
  },
]

const SampleReportHero: React.FC = () => (
  // `@container`: above `lg` this panel is the leftover of a split with a
  // fixed-width form column, so the card's width stops tracking the viewport's
  // and the tiles below have to answer to the card.
  <div className="@container flex flex-col gap-5 rounded-2xl bg-neutral-0 bg-gradient-to-b from-yellow-25/90 to-moss-25/0 p-6 shadow-[0px_2px_4px_-1px_rgba(0,0,0,0.06),0px_4px_6px_-1px_rgba(0,0,0,0.10)]">
    <div className="flex items-baseline justify-between gap-3">
      <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
        California · Therapist · Sole proprietor
      </Text>
      <span className="shrink-0">
        <Text variant={TypographyStyles.BODY_XS} color={TextColors.MUTED}>
          Sample
        </Text>
      </span>
    </div>

    <Text variant={TypographyStyles.DISPLAY_SM} color={TextColors.HEADER}>
      3 things are worth a look. The rest is handled.
    </Text>

    <Text variant={TypographyStyles.BODY_SM} className="text-neutral-600">
      We reviewed 14 obligations across your practice. 3 need a look, 9 are
      already covered, 2 are worth keeping an eye on.
    </Text>

    {/* `min-w-0` because a flex item will not otherwise shrink below its own
        min-content, and these ran out past the card's edge. */}
    <div className="flex flex-col gap-3 @sm:flex-row">
      {TILES.map(({ count, label, icon, color, bg }) => (
        <div
          key={label}
          className="flex min-w-0 flex-1 flex-col gap-3 rounded-xl border border-neutral-100 bg-neutral-0 p-4"
        >
          <div className="flex items-center gap-3">
            <Icon
              icon={icon}
              size="sm"
              color={color}
              backgroundColor="transparent"
              backgroundClassName={bg}
              backgroundStyle="rounded"
            />
            {/* Raw rather than `Text`: this mirrors the report's own
                `CountTile`, which sets the count at `font-primary text-3xl`,
                a size no `TypographyStyles` variant carries. Matching it
                exactly is the point of the mock. */}
            <span className="font-primary text-3xl text-neutral-900">
              {count}
            </span>
          </div>
          <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
            {label}
          </Text>
        </div>
      ))}
    </div>
  </div>
)

/**
 * The signup screen's right-hand panel: what the account is actually for.
 *
 * Replaces the decorative illustration that used to sit here. Someone arrives
 * cold from a marketing link and is being asked for an email and a password
 * before we've earned anything. Showing the thing they get, next to the form
 * asking for it, does more than another line of copy would.
 */
export const ReadinessPreviewPanel: React.FC = () => (
  <div className="flex w-full max-w-[520px] flex-col gap-8">
    <div className="flex flex-col gap-3">
      {/* `neutral-600` has no `TextColors` token; the DS passes it through
          `className` the same way (see `list-custom-bullets`, `hero-card`). */}
      <Text variant={TypographyStyles.EYEBROW} className="text-neutral-600">
        See where your practice stands
      </Text>

      <SampleReportHero />
    </div>

    {/* A real list, styled two ways. Stacked on a phone it reads as one — bulleted, at
        body-md so it is not the smallest type on a screen that is now mostly this panel.
        From `sm` it goes back to three columns, where markers would be noise and the
        smaller body keeps the three cells even.

        No rule above each point any more: in the three-column layout it read as a header
        underline, but the same border stacked into a single column reads as dividers
        chopping the panel up, and the gap already separates them. */}
    <List
      bulleted
      className="pl-5 sm:grid sm:list-none sm:grid-cols-3 sm:gap-6 sm:pl-0"
    >
      {POINTS.map(({ title, body }) => (
        // Item spacing lives on the item, not as `space-y` on the list: the margin that
        // has to override `List.Item`'s own `mb-2` would otherwise also beat the parent's
        // `space-y` rule — same specificity, later utility wins — and silently flatten the
        // gap between points to zero. `sm:mb-0` hands spacing back to the grid.
        <List.Item
          key={title}
          className="mb-6 space-y-1 last:mb-0 sm:mb-0 sm:space-y-2"
        >
          {/* `label-md` on a phone, pinned back to `label-sm`'s 14px from `sm`. Same
              technique as the body below, and for the same reason — the two tokens
              differ only in font size, and neither can vary by breakpoint on its own. */}
          <Text
            variant={TypographyStyles.LABEL_MD}
            color={TextColors.HEADER}
            className="sm:text-[14px]"
          >
            {title}
          </Text>
          {/* `body-md` with the font size pinned back to 14px from `sm`. Not
              `TypographyStyles.BODY_SM`, which cannot vary by breakpoint, and not
              `sm:text-sm`, which would also reset the line height — the two tokens are
              identical apart from their font size, so overriding just that leaves the
              desktop rendering untouched. */}
          <Text
            variant={TypographyStyles.BODY_MD}
            color={TextColors.MUTED}
            className="sm:text-[14px]"
          >
            {body}
          </Text>
        </List.Item>
      ))}
    </List>

    <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
      Personalized to your state, your license, and how your practice is set up.
    </Text>
  </div>
)
